import { Capacitor } from '@capacitor/core';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import { useUpdateStore } from '@/stores/updateStore';

/** Compare two dotted versions ("1.2.3"). Returns -1/0/1 for a<b / a==b / a>b.
 *  Missing segments count as 0, so "1.2" == "1.2.0". Non-numeric ⇒ treated 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.');
  const pb = b.split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = parseInt(pa[i] ?? '0', 10) || 0;
    const nb = parseInt(pb[i] ?? '0', 10) || 0;
    if (na !== nb) return na < nb ? -1 : 1;
  }
  return 0;
}

function isNative(): boolean {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android';
}

/**
 * Fetch the server's version config and decide whether to show an update gate.
 * Native only — the web build always serves the latest bundle on refresh, so
 * there is nothing to "update". Never throws; on any failure it stays silent
 * so a flaky network can't lock users out.
 */
export async function runUpdateCheck(): Promise<void> {
  if (!isNative()) return;
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    const current = info.version; // marketing version (CFBundleShortVersionString / versionName)

    const res = await fetch(`${SERVER_URL}/config`, { headers: COMMON_HEADERS });
    if (!res.ok) return;
    const { minVersion, latestVersion } = (await res.json()) as {
      minVersion?: string;
      latestVersion?: string;
    };

    if (minVersion && compareVersions(current, minVersion) < 0) {
      useUpdateStore.getState().setGate('hard');
    } else if (latestVersion && compareVersions(current, latestVersion) < 0) {
      useUpdateStore.getState().setGate('soft');
    }
  } catch {
    /* offline / parse error → stay silent */
  }
}

/** Open the platform's store page so the user can update. */
export async function openStorePage(): Promise<void> {
  const url =
    Capacitor.getPlatform() === 'ios'
      ? 'https://apps.apple.com/app/id6778849374'
      : 'https://play.google.com/store/apps/details?id=games.dontmiss.app';
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } catch {
    window.open(url, '_blank');
  }
}
